import {
  Button,
  Modal,
  notification,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useMemo, useState } from 'react';

import {
  useMergeDuplicateRates,
  useRateUpdate,
} from '../../hooks/apiHooks/rate';
import { AuditDuplicateGroup, Rate } from '../../models/Rate';

const { Text, Title } = Typography;

interface IProps {
  group: AuditDuplicateGroup;
  carrierId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const MergeRatesModal: React.FC<IProps> = ({
  carrierId,
  group,
  onClose,
  onSuccess,
}) => {
  const mergeMutation = useMergeDuplicateRates();
  const updateMutation = useRateUpdate();

  const [selectedBaseRateId, setSelectedBaseRateId] = useState<number>(
    () => Math.max(...group.rates.map((r) => r.id)) // newest rate id to match API
  );
  const baseRate =
    group.rates.find((r) => r.id === selectedBaseRateId) ?? group.rates[0];

  // helper to find the rate with the latest updatedAt for a given class
  const findLatestRateForClass = useCallback(
    (classId: number, value?: number): Rate | undefined => {
      let latestRate: Rate | undefined = undefined;
      let latestUpdatedAt: Date | null = null;

      for (const rate of group.rates) {
        const classRate = rate.classRate?.[classId];
        if (
          classRate?.value != null &&
          (value === undefined || classRate.value === value)
        ) {
          const updatedAt = classRate.updatedAt
            ? new Date(classRate.updatedAt)
            : rate.updatedAt
            ? new Date(rate.updatedAt)
            : null;

          if (!latestUpdatedAt || (updatedAt && updatedAt > latestUpdatedAt)) {
            latestUpdatedAt = updatedAt;
            latestRate = rate;
          }
        }
      }

      return latestRate;
    },
    [group.rates]
  );

  const initialMergedPreview = useMemo(() => {
    const baseRate = group.rates.reduce((newest, rate) =>
      rate.id > newest.id ? rate : newest
    );

    const allClassIds = new Set<number>();
    group.rates.forEach((rate) => {
      Object.keys(rate.classRate || {}).forEach((classId) => {
        allClassIds.add(parseInt(classId));
      });
    });

    // For each class, find the rate with the latest updatedAt
    const mergedClassRates: {
      [classId: number]: { name: string; rateId: number; value: number };
    } = {};

    allClassIds.forEach((classId) => {
      const latestRate = findLatestRateForClass(classId);
      if (latestRate) {
        const classRate = latestRate.classRate?.[classId];
        if (classRate) {
          mergedClassRates[classId] = {
            name: classRate.name || `Class ${classId}`,
            rateId: latestRate.id,
            value: classRate.value,
          };
        }
      }
    });

    return { baseRate, mergedClassRates };
  }, [group, findLatestRateForClass]);

  const [editedClassRates, setEditedClassRates] = useState<{
    [classId: number]: { rateId: number; value: number };
  }>(() => {
    const initial: { [classId: number]: { rateId: number; value: number } } =
      {};
    Object.entries(initialMergedPreview.mergedClassRates).forEach(
      ([classId, classRate]) => {
        initial[parseInt(classId)] = {
          rateId: classRate.rateId,
          value: classRate.value,
        };
      }
    );
    return initial;
  });

  const handleClassRateChange = (classId: number, value: number) => {
    const latestRate = findLatestRateForClass(classId, value);
    if (latestRate) {
      setEditedClassRates((prev) => ({
        ...prev,
        [classId]: { rateId: latestRate.id, value },
      }));
    }
  };

  // get values for each class from the rates being merged
  const getAvailableClassValues = (classId: number): number[] => {
    const values = new Set<number>();
    group.rates.forEach((rate) => {
      const classRate = rate.classRate?.[classId];
      if (classRate?.value != null) {
        values.add(classRate.value);
      }
    });
    return Array.from(values).sort((a, b) => a - b);
  };

  const handleMerge = async () => {
    try {
      const selectedRateClasses: { [classId: number]: number } = {};
      Object.entries(editedClassRates).forEach(([classId, edited]) => {
        selectedRateClasses[parseInt(classId)] = edited.rateId;
      });

      // merge rates
      const mergedRate = await mergeMutation.mutateAsync({
        carrierId,
        destination: group.destination,
        destinationShipTypeId: group.destinationShipTypeId,
        modeId: group.modeId,
        origin: group.origin,
        selectedRateClasses,
        selectedRateId: selectedBaseRateId,
        shipTypeId: group.shipTypeId,
      });

      // check if any class rates were edited
      const hasEdits = Object.entries(editedClassRates).some(
        ([classId, edited]) => {
          const initial =
            initialMergedPreview.mergedClassRates[parseInt(classId)];
          return edited.value !== initial?.value;
        }
      );

      // if class rates were edited, update the merged rate
      if (hasEdits) {
        const updatedClassRate = { ...mergedRate.classRate };
        Object.entries(editedClassRates).forEach(([classId, edited]) => {
          const classIdNum = parseInt(classId);
          const existingClassRate = mergedRate.classRate[classIdNum];
          const classInfo = initialMergedPreview.mergedClassRates[classIdNum];

          if (existingClassRate) {
            updatedClassRate[classIdNum] = {
              ...existingClassRate,
              value: edited.value,
            };
          } else if (classInfo) {
            updatedClassRate[classIdNum] = {
              name: classInfo.name,
              value: edited.value,
            };
          }
        });

        await updateMutation.mutateAsync({
          ...mergedRate,
          classRate: updatedClassRate,
        });
      }

      notification.success({
        description: 'Duplicate rates merged successfully.',
        message: 'Success',
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      notification.error({
        description: error?.message || 'Failed to merge duplicate rates.',
        message: 'Error',
      });
    }
  };

  // rates table columns
  const classIds = Array.from(
    new Set(
      group.rates.flatMap((rate) =>
        Object.keys(rate.classRate || {}).map((id) => parseInt(id))
      )
    )
  );

  const classColumns: ColumnsType<Rate> = classIds.map((classId) => {
    let className = `Class ${classId}`;
    for (const rate of group.rates) {
      const name = rate.classRate?.[classId]?.name;
      if (name) {
        className = name;
        break;
      }
    }
    return {
      ellipsis: true,
      key: `class_${classId}`,
      render: (_, rate) => {
        const value = rate.classRate?.[classId]?.value;
        return value != null ? `$${value.toFixed(2)}` : '-';
      },
      title: className,
    };
  });

  const rateColumns: ColumnsType<Rate> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Rate ID',
      width: 100,
    },
    ...classColumns,
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
      title: 'Updated At',
      width: 100,
    },
  ];

  return (
    <Modal
      centered
      open
      footer={null}
      maskClosable={false}
      style={{ maxWidth: '80vw' }}
      title="Merge Duplicate Rates"
      width="auto"
      onCancel={onClose}
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Route: </Text>
          <Text>{group.origin}</Text>
          {group.rates[0]?.originShipTypeName && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {group.rates[0].originShipTypeName}
            </Tag>
          )}
          <Text style={{ margin: '0 8px' }}>→</Text>
          <Text>{group.destination}</Text>
          {group.rates[0]?.destinationShipTypeName && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {group.rates[0].destinationShipTypeName}
            </Tag>
          )}
          {group.rates[0]?.modeName && (
            <Tag color="green" style={{ marginLeft: 8 }}>
              {group.rates[0].modeName}
            </Tag>
          )}
        </div>
        <Text style={{ display: 'block', marginBottom: 12 }} type="secondary">
          The following {group.rates.length} duplicate rates will be merged and
          the selected rate (ID: {baseRate.id}) will be kept:
        </Text>
        {/* InitialRates */}
        <div style={{ display: 'inline-block', width: 'max-content' }}>
          <Table
            bordered
            columns={rateColumns}
            dataSource={group.rates}
            pagination={false}
            rowKey={(rate) => rate.id}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </div>
      </div>

      <div style={{ marginBottom: 72 }}>
        <Title level={5}>Result</Title>
        <Text style={{ display: 'block', marginBottom: 12 }} type="secondary">
          Review and edit the merged rate values below.
        </Text>

        {/* Result */}
        <div style={{ display: 'inline-block', width: 'max-content' }}>
          <Table
            bordered
            columns={[
              {
                dataIndex: 'id',
                key: 'id',
                render: () => (
                  <Select
                    style={{ minWidth: 100 }}
                    value={selectedBaseRateId}
                    onChange={setSelectedBaseRateId}
                  >
                    {group.rates.map((rate) => (
                      <Select.Option key={rate.id} value={rate.id}>
                        {rate.id}
                      </Select.Option>
                    ))}
                  </Select>
                ),
                title: 'Rate ID',
                width: 100,
              },
              ...classIds.map((classId) => {
                const classInfo =
                  initialMergedPreview.mergedClassRates[classId];
                const className = classInfo?.name || `Class ${classId}`;
                const editedData = editedClassRates[classId];
                const currentValue = editedData?.value ?? classInfo?.value ?? 0;
                const availableValues = getAvailableClassValues(classId);

                return {
                  key: `class_${classId}`,
                  render: () => {
                    const handleChange = (value: number) => {
                      handleClassRateChange(classId, value);
                    };
                    return (
                      <Select value={currentValue} onChange={handleChange}>
                        {availableValues.map((value) => (
                          <Select.Option key={value} value={value}>
                            ${value.toFixed(2)}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  },
                  title: className,
                };
              }),
            ]}
            dataSource={[{ id: baseRate.id }]}
            pagination={false}
            rowKey={() => 'preview'}
            size="small"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          loading={mergeMutation.isLoading || updateMutation.isLoading}
          type="primary"
          onClick={handleMerge}
        >
          Confirm Merge
        </Button>
      </div>
    </Modal>
  );
};

export default MergeRatesModal;
